<!-- src/components/Inventario/Reportes/MermasVenaScrap/components/MVSGraficaBarras.vue -->
<template>
  <div class="mvs-grafica-wrapper" :style="{ height: alturaWrapper }">
    <canvas v-show="datos.length" ref="canvasRef"></canvas>
    <div v-if="!datos.length" class="mvs-grafica-empty">
      Sin datos para el periodo / máquinas seleccionadas
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables, ChartDataLabels);

const props = defineProps({
  datos: {
    type: Array,
    default: () => [], // [{ maquina: 'IMMI01', scrap: 7, vena: 7 }, ...]
  },
  titulo: {
    type: String,
    default: "Generación de Vena y Scrap Kg",
  },
  subtitulo: {
    type: String,
    default: "",
  },
});

const COLOR_SCRAP = "#c0392b";
const COLOR_VENA = "#f2a900";

// Breakpoint compartido con el layout (.mvs-dashboard__body) en mermas-vena-scrap.css
const MEDIA_QUERY_HORIZONTAL = "(max-width: 991px)";

const canvasRef = ref(null);
let chartInstance = null;

const mediaQuery =
  typeof window !== "undefined" ? window.matchMedia(MEDIA_QUERY_HORIZONTAL) : null;
const esHorizontal = ref(mediaQuery ? mediaQuery.matches : false);

function actualizarMediaQuery(e) {
  esHorizontal.value = e.matches;
}

// En modo horizontal cada máquina necesita su propio renglón, así que la
// altura del contenedor crece con el número de barras en vez de quedar fija.
const alturaWrapper = computed(() => {
  if (!esHorizontal.value) return "380px";
  const alto = Math.max(320, props.datos.length * 46 + 80);
  return `${alto}px`;
});

function buildConfig() {
  const ejeCategoria = esHorizontal.value ? "y" : "x";
  const ejeValor = esHorizontal.value ? "x" : "y";

  // Objeto plano construido desde cero en cada llamada — nunca se reutiliza
  // ni se le pasa a Chart.js una referencia que Vue esté observando.
  return {
    type: "bar",
    data: {
      labels: props.datos.map((d) => d.maquina),
      datasets: [
        {
          label: "Scrap",
          data: props.datos.map((d) => d.scrap),
          backgroundColor: COLOR_SCRAP,
          stack: "mermas",
        },
        {
          label: "Vena",
          data: props.datos.map((d) => d.vena),
          backgroundColor: COLOR_VENA,
          stack: "mermas",
        },
      ],
    },
    options: {
      indexAxis: ejeCategoria,
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: props.titulo,
          font: { size: 15, weight: "bold" },
        },
        subtitle: {
          display: !!props.subtitulo,
          text: props.subtitulo,
          font: { size: 12 },
          padding: { bottom: 8 },
        },
        legend: {
          display: true,
          position: "bottom",
          labels: { usePointStyle: true },
        },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.dataset.label}: ${
                esHorizontal.value ? ctx.parsed.x : ctx.parsed.y
              } Kg`,
          },
        },
        datalabels: {
          color: "#fff",
          font: { weight: "bold", size: 11 },
          formatter: (value) => (value > 0 ? value : ""),
        },
      },
      scales: {
        [ejeCategoria]: { stacked: true, grid: { display: false } },
        [ejeValor]: {
          stacked: true,
          beginAtZero: true,
          title: { display: true, text: "Kg" },
        },
      },
    },
  };
}

function destroyChart() {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
}

function renderChart() {
  destroyChart();
  if (!canvasRef.value || !props.datos.length) return;
  chartInstance = new Chart(canvasRef.value, buildConfig());
}

onMounted(() => {
  // nextTick garantiza que el canvas ya está en el DOM real antes de
  // que Chart.js intente adjuntarse a él.
  nextTick(renderChart);
  mediaQuery?.addEventListener("change", actualizarMediaQuery);
});

onBeforeUnmount(() => {
  destroyChart();
  mediaQuery?.removeEventListener("change", actualizarMediaQuery);
});

// Patrón correcto para Chart.js + Vue 3:
// 1. destroyChart() SÍNCRONO → Chart.js libera el canvas ANTES de que Vue parchee el DOM.
// 2. nextTick(renderChart) → Vue termina de reconciliar el DOM, luego creamos la instancia.
// Si se hace al revés (await nextTick primero), Vue parchea mientras Chart.js todavía
// tiene referencias internas al canvas → vnode null → "Cannot read properties of null".
watch(
  () => [props.datos, props.titulo, props.subtitulo, esHorizontal.value],
  () => {
    destroyChart(); // síncrono: libera Chart.js antes del patch de Vue
    nextTick(renderChart); // asíncrono: renderiza después del patch de Vue
  },
  { deep: true }
);
</script>

<style scoped>
.mvs-grafica-wrapper {
  position: relative;
  width: 100%;
  transition: height 0.15s ease;
}

.mvs-grafica-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 0.9rem;
}
</style>
